import { HttpClient, HttpEvent, HttpParams, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { apis } from '../../../environments/environment'
import { ApiEndpoint } from './api-endpoint'

/**
 * The single generic HTTP-wrapping service in the app. Every domain facade
 * (core/api/<domain>/*.service.ts) calls through this instead of injecting
 * HttpClient directly -- enforced by the no-restricted-imports ESLint rule.
 */
@Injectable({ providedIn: 'root' })
export class RgHttpService {
    private readonly baseUrl = apis.baseUrl

    constructor (private http: HttpClient) { }

    /**
     * Laravel Sanctum's SPA cookie-auth handshake -- must be fetched once
     * before the first login/register call. Hits the app root (not the
     * versioned /api/v4 baseUrl), so it's a hand-written exception rather
     * than a normal ApiEndpoint.
     *
     * Sanctum's own route has no path prefix in the Laravel app itself --
     * any path segment before `/api/vN` in baseUrl (e.g. `/backend` in
     * production, or nothing at all for a bare local `php artisan serve`)
     * is purely an external reverse-proxy prefix, so it's derived by
     * stripping the `/api/vN` suffix rather than hardcoded.
     */
    getCsrfCookie (): Observable<void> {
        const rootUrl = this.baseUrl.replace(/\/api\/v\d+\/?$/, '')
        return this.http.get<void>(`${rootUrl}/sanctum/csrf-cookie`, { withCredentials: true })
    }

    call<TRequest = void, TResponse = void> (
        ep: ApiEndpoint<TRequest, TResponse>,
        params?: TRequest
    ): Observable<TResponse> {
        const { url, remaining } = this.resolvePathParams(ep.url, params)
        const fullUrl = `${this.baseUrl}/${url}`

        switch (ep.method) {
        case 'GET':
            return this.http.get<TResponse>(fullUrl, { params: this.toHttpParams(remaining) })
        case 'DELETE':
            return this.http.delete<TResponse>(fullUrl, { params: this.toHttpParams(remaining) })
        case 'POST':
            return this.http.post<TResponse>(fullUrl, remaining)
        case 'PUT':
            return this.http.put<TResponse>(fullUrl, remaining)
        }
    }

    /**
     * For the rare DELETE endpoint that takes a JSON body instead of query
     * params (Angular's HttpClient supports this via the `body` option, but
     * call() always treats DELETE's params as a query string).
     */
    callDeleteWithBody<TRequest = void, TResponse = void> (
        ep: ApiEndpoint<TRequest, TResponse>,
        body?: TRequest
    ): Observable<TResponse> {
        const { url, remaining } = this.resolvePathParams(ep.url, body)
        const fullUrl = `${this.baseUrl}/${url}`
        return this.http.delete<TResponse>(fullUrl, { body: remaining })
    }

    /**
     * For endpoints that return a binary payload (file downloads/conversions)
     * instead of the standard Resp<T> JSON envelope. Same url/params
     * resolution as call(), different responseType.
     */
    callForBlob<TRequest = void> (
        ep: ApiEndpoint<TRequest, unknown>,
        params?: TRequest
    ): Observable<Blob> {
        const { url, remaining } = this.resolvePathParams(ep.url, params)
        const fullUrl = `${this.baseUrl}/${url}`

        switch (ep.method) {
        case 'GET':
            return this.http.get(fullUrl, { params: this.toHttpParams(remaining), responseType: 'blob' })
        case 'DELETE':
            return this.http.delete(fullUrl, { params: this.toHttpParams(remaining), responseType: 'blob' })
        case 'POST':
            return this.http.post(fullUrl, remaining, { responseType: 'blob' })
        case 'PUT':
            return this.http.put(fullUrl, remaining, { responseType: 'blob' })
        }
    }

    /**
     * Like callForBlob(), but returns the full HttpResponse (headers + body)
     * instead of just the Blob body -- for the rare download endpoint where
     * the caller needs response headers too (e.g. a filename from
     * Content-Disposition, or to distinguish a JSON error body from the
     * expected binary payload).
     */
    callForBlobResponse<TRequest = void> (
        ep: ApiEndpoint<TRequest, unknown>,
        params?: TRequest
    ): Observable<HttpResponse<Blob>> {
        const { url, remaining } = this.resolvePathParams(ep.url, params)
        const fullUrl = `${this.baseUrl}/${url}`

        switch (ep.method) {
        case 'GET':
            return this.http.get(fullUrl, { params: this.toHttpParams(remaining), responseType: 'blob', observe: 'response' })
        case 'DELETE':
            return this.http.delete(fullUrl, { params: this.toHttpParams(remaining), responseType: 'blob', observe: 'response' })
        case 'POST':
            return this.http.post(fullUrl, remaining, { responseType: 'blob', observe: 'response' })
        case 'PUT':
            return this.http.put(fullUrl, remaining, { responseType: 'blob', observe: 'response' })
        }
    }

    /**
     * For generic, dynamically-targeted upload widgets (e.g. rg-file-uploader)
     * that accept an arbitrary destination path from their consumer rather
     * than pointing at one fixed contract, and need upload-progress events.
     */
    postFormDataWithProgress (relativeUrl: string, formData: FormData): Observable<HttpEvent<any>> {
        return this.http.post(`${this.baseUrl}/${relativeUrl}`, formData, {
            reportProgress: true,
            observe: 'events'
        })
    }

    /**
     * Generic escape hatch for infrastructure like RgGridComponent that is
     * itself reused across ~100+ different list screens, each pointing it at
     * a different backend resource path chosen by the CALLER at runtime --
     * there is no single fixed ApiEndpoint to declare here. Prefer call()
     * with a real ApiEndpoint for anything that has one fixed backend route.
     */
    postToResource<TResponse = any> (relativeUrl: string, body: unknown): Observable<TResponse> {
        return this.http.post<TResponse>(`${this.baseUrl}/${relativeUrl}`, body)
    }

    postToResourceForBlob (relativeUrl: string, body: unknown): Observable<Blob> {
        return this.http.post(`${this.baseUrl}/${relativeUrl}`, body, { responseType: 'blob' })
    }

    /**
     * Generic escape hatch for callers (e.g. rg-file-uploader) that delete a
     * caller-supplied dynamic resource path rather than a single fixed
     * ApiEndpoint.
     */
    deleteResource<TResponse = any> (relativeUrl: string, params?: Record<string, any>): Observable<TResponse> {
        return this.http.delete<TResponse>(`${this.baseUrl}/${relativeUrl}`, { params: this.toHttpParams(params ?? {}) })
    }

    /**
     * Generic escape hatch for the workshop-panel cruds/ scaffold (and
     * similar dynamic-resource callers), which is itself reused across ~40
     * different CRUD screens by setting `resource` to a different backend
     * path at runtime -- there is no single fixed ApiEndpoint to declare
     * for something whose path is chosen by the caller.
     */
    getResource<TResponse = any> (relativeUrl: string, params?: Record<string, any>): Observable<TResponse> {
        return this.http.get<TResponse>(`${this.baseUrl}/${relativeUrl}`, { params: this.toHttpParams(params ?? {}) })
    }

    putToResource<TResponse = any> (relativeUrl: string, body: unknown): Observable<TResponse> {
        return this.http.put<TResponse>(`${this.baseUrl}/${relativeUrl}`, body)
    }

    /**
     * Substitutes any ':paramName' tokens in the url with values from params,
     * returning the resolved url plus whatever params were NOT consumed as
     * path tokens (these become query params for GET/DELETE, or the request
     * body for POST/PUT).
     */
    private resolvePathParams (url: string, params: unknown): { url: string, remaining: any } {
        // FormData isn't a plain object -- spreading it would silently drop
        // its entries, so pass it straight through with no path-token
        // substitution (none of our FormData-bodied endpoints use :params).
        if (params instanceof FormData) {
            return { url, remaining: params }
        }

        const remaining: Record<string, any> = { ...(params as Record<string, any> ?? {}) }

        const resolvedUrl = url.replace(/:([a-zA-Z0-9_]+)/g, (_match, key: string) => {
            const value = remaining[key]
            delete remaining[key]
            return encodeURIComponent(String(value))
        })

        return { url: resolvedUrl, remaining }
    }

    private toHttpParams (params: Record<string, any>): HttpParams {
        let httpParams = new HttpParams()

        for (const key of Object.keys(params)) {
            const value = params[key]
            if (value === undefined || value === null) {
                continue
            }

            if (Array.isArray(value)) {
                for (const item of value) {
                    httpParams = httpParams.append(key, String(item))
                }
            }
            else {
                httpParams = httpParams.set(key, String(value))
            }
        }

        return httpParams
    }
}
